import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, LogOut, Image as ImageIcon, Mail, ExternalLink, FileText, Eye, Send, Save, Upload, X, Star } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STATUSES = [
  { key: "new", label: "New", color: "bg-[#00B4D8]/15 text-[#0F3A5A] border-[#00B4D8]/30" },
  { key: "contacted", label: "Contacted", color: "bg-[#0F3A5A]/10 text-[#0F3A5A] border-[#0F3A5A]/25" },
  { key: "estimate_sent", label: "Estimate Sent", color: "bg-violet-100 text-violet-700 border-violet-200" },
  { key: "won", label: "Won", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { key: "lost", label: "Lost", color: "bg-slate-100 text-slate-600 border-slate-200" },
];

function statusMeta(key) {
  return STATUSES.find((s) => s.key === key) || STATUSES[0];
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [activeQuote, setActiveQuote] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editStatus, setEditStatus] = useState("new");
  const [editNotes, setEditNotes] = useState("");
  const [savingCrm, setSavingCrm] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    category: "",
    before_url: "",
    after_url: "",
    description: "",
  });
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [singleFiles, setSingleFiles] = useState([]); // for "single" carousel format
  const [singleUrls, setSingleUrls] = useState([""]); // for URL-based "single" mode
  const [displayFormat, setDisplayFormat] = useState("single"); // "single" | "slider" | "swipe"
  const [uploadMode, setUploadMode] = useState("upload"); // "upload" | "url"
  const [uploading, setUploading] = useState(false);

  const loadAll = async () => {
    try {
      const [meRes, leadsRes, galRes, quotesRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/leads"),
        api.get("/gallery"),
        api.get("/quote-requests"),
      ]);
      setUser(meRes.data);
      setLeads(leadsRes.data || []);
      setGallery(galRes.data || []);
      setQuotes(quotesRes.data || []);
    } catch (e) {
      navigate("/admin/login");
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("az_token");
    navigate("/admin/login");
  };

  const resetGalleryForm = () => {
    setForm({ title: "", category: "", before_url: "", after_url: "", description: "" });
    setBeforeFile(null);
    setAfterFile(null);
    setSingleFiles([]);
    setSingleUrls([""]);
    setDisplayFormat("single");
    setUploadMode("upload");
  };

  const addGallery = async (e) => {
    e.preventDefault();
    if (uploading) return;
    setUploading(true);
    try {
      let data;
      if (uploadMode === "upload") {
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("category", form.category);
        fd.append("description", form.description);
        fd.append("display_format", displayFormat);

        if (displayFormat === "single") {
          if (singleFiles.length === 0) {
            toast.error("Please select at least one photo");
            setUploading(false);
            return;
          }
          singleFiles.forEach((f) => fd.append("photos", f));
        } else {
          if (!beforeFile || !afterFile) {
            toast.error("Please select both Before and After photos");
            setUploading(false);
            return;
          }
          fd.append("before_photo", beforeFile);
          fd.append("after_photo", afterFile);
        }

        const res = await api.post("/gallery/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        data = res.data;
      } else {
        // URL mode
        const payload = {
          title: form.title,
          category: form.category,
          description: form.description,
          display_format: displayFormat,
        };
        if (displayFormat === "single") {
          const urls = singleUrls.map((u) => u.trim()).filter(Boolean);
          if (urls.length === 0) {
            toast.error("Add at least one image URL");
            setUploading(false);
            return;
          }
          payload.images = urls;
        } else {
          if (!form.before_url || !form.after_url) {
            toast.error("Before and After URLs are required");
            setUploading(false);
            return;
          }
          payload.before_url = form.before_url;
          payload.after_url = form.after_url;
        }
        const res = await api.post("/gallery", payload);
        data = res.data;
      }
      setGallery((g) => [data, ...g]);
      resetGalleryForm();
      setOpen(false);
      toast.success("Project added to gallery");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to add project");
    } finally {
      setUploading(false);
    }
  };

  const removeGallery = async (id) => {
    if (!confirm("Delete this project?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      setGallery((g) => g.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleFeatured = async (item) => {
    const next = !item.featured;
    try {
      const { data } = await api.patch(`/gallery/${item.id}`, { featured: next });
      setGallery((g) => g.map((x) => (x.id === item.id ? { ...x, featured: data.featured } : x)));
      toast.success(next ? "Featured on homepage" : "Removed from featured");
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="grid place-items-center w-9 h-9 rounded-lg bg-[#0F3A5A] text-white font-display font-bold text-sm">
              AZ
            </div>
            <div>
              <p className="font-display font-semibold text-slate-900 leading-tight">AZ Pools Admin</p>
              <p className="text-[11px] text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={logout}
            data-testid="logout-btn"
            className="rounded-full"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900 mb-2">
          Dashboard
        </h1>
        <p className="text-slate-500 mb-8">Manage leads and project gallery.</p>

        <div className="grid sm:grid-cols-4 gap-4 mb-10">
          <StatCard label="Quote Requests" value={quotes.length} icon={<FileText />} />
          <StatCard label="Contact Leads" value={leads.length} icon={<Mail />} />
          <StatCard label="Gallery Projects" value={gallery.length} icon={<ImageIcon />} />
          <StatCard
            label="Emails Sent"
            value={
              quotes.filter((q) => q.email_sent).length +
              leads.filter((l) => l.email_sent).length
            }
            icon={<Mail />}
          />
        </div>

        <Tabs defaultValue="quotes">
          <TabsList>
            <TabsTrigger value="quotes" data-testid="tab-quotes">Quote Requests</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Contact Leads</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="site-images" data-testid="tab-site-images">Site Images</TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="mt-6">
            {/* Pipeline filter + digest button */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div className="flex flex-wrap gap-2" data-testid="quote-status-filter">
                <button
                  onClick={() => setStatusFilter("all")}
                  data-testid="filter-all"
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition ${
                    statusFilter === "all"
                      ? "bg-[#0F3A5A] border-[#0F3A5A] text-white"
                      : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                  }`}
                >
                  All <span className="opacity-70 ml-1">{quotes.length}</span>
                </button>
                {STATUSES.map((s) => {
                  const count = quotes.filter((q) => (q.status || "new") === s.key).length;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setStatusFilter(s.key)}
                      data-testid={`filter-${s.key}`}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition ${
                        statusFilter === s.key
                          ? "bg-[#0F3A5A] border-[#0F3A5A] text-white"
                          : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                      }`}
                    >
                      {s.label} <span className="opacity-70 ml-1">{count}</span>
                    </button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                disabled={sendingDigest}
                onClick={async () => {
                  setSendingDigest(true);
                  try {
                    const { data } = await api.post("/quote-requests/digest", null, { params: { days: 1 } });
                    if (data?.ok) {
                      toast.success("Daily summary sent", {
                        description: `${data.total} quote${data.total === 1 ? "" : "s"} in the last 24 hours.`,
                      });
                    } else {
                      toast.error("Digest skipped", { description: data?.reason || "Email not configured" });
                    }
                  } catch (err) {
                    toast.error("Failed to send digest");
                  } finally {
                    setSendingDigest(false);
                  }
                }}
                className="rounded-full"
                data-testid="send-digest-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingDigest ? "Sending…" : "Send Daily Summary"}
              </Button>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Timeline</TableHead>
                    <TableHead>Photos</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const filtered = quotes.filter(
                      (q) => statusFilter === "all" || (q.status || "new") === statusFilter
                    );
                    if (filtered.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center text-slate-400 py-12">
                            No quotes in this view.
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return filtered.map((q) => {
                      const meta = statusMeta(q.status || "new");
                      return (
                        <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                          <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                            {new Date(q.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{q.name}</p>
                            <p className="text-xs text-slate-500">{q.email}</p>
                          </TableCell>
                          <TableCell>
                            <a href={`tel:${q.phone}`} className="text-[#0F3A5A] hover:underline">{q.phone}</a>
                          </TableCell>
                          <TableCell>{q.city}</TableCell>
                          <TableCell>{q.service_interest}</TableCell>
                          <TableCell>
                            <span
                              className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold whitespace-nowrap ${meta.color}`}
                              data-testid={`status-pill-${q.id}`}
                            >
                              {meta.label}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs">{q.timeline || "—"}</TableCell>
                          <TableCell>
                            {q.photo_count > 0 ? (
                              <span className="text-xs px-2 py-1 rounded-full bg-[#00B4D8]/10 text-[#0F3A5A] font-medium">
                                {q.photo_count} 📎
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={async () => {
                                try {
                                  const { data } = await api.get(`/quote-requests/${q.id}`);
                                  setActiveQuote(data);
                                  setEditStatus(data.status || "new");
                                  setEditNotes(data.admin_notes || "");
                                } catch (_) {
                                  toast.error("Failed to load");
                                }
                              }}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F3A5A] hover:text-[#00B4D8]"
                              data-testid={`view-quote-${q.id}`}
                            >
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Email Sent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-400 py-12">
                        No contact leads yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {leads.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                        {new Date(l.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{l.name}</p>
                        <p className="text-xs text-slate-500">{l.email}</p>
                      </TableCell>
                      <TableCell>
                        <a href={`tel:${l.phone}`} className="text-[#0F3A5A] hover:underline">
                          {l.phone}
                        </a>
                      </TableCell>
                      <TableCell>{l.city}</TableCell>
                      <TableCell>{l.service}</TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-sm text-slate-600 line-clamp-2">{l.description}</p>
                      </TableCell>
                      <TableCell>
                        {l.email_sent ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                            Sent
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">
                            Skipped
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="gallery" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-[#0F3A5A] hover:bg-[#174E7A]" data-testid="add-project-btn">
                    <Plus className="w-4 h-4 mr-1.5" />
                    Add Project
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="add-project-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-display">Add Gallery Project</DialogTitle>
                    <DialogDescription>
                      Pick a display format, then upload photos or paste URLs.
                    </DialogDescription>
                  </DialogHeader>

                  {/* Display format selector */}
                  <div className="space-y-2">
                    <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
                      Display Format
                    </Label>
                    <div className="grid grid-cols-3 gap-2" data-testid="format-selector">
                      <FormatTile
                        active={displayFormat === "single"}
                        onClick={() => setDisplayFormat("single")}
                        testid="format-single"
                        icon="🖼️"
                        label="Single Carousel"
                        hint="Multiple photos"
                      />
                      <FormatTile
                        active={displayFormat === "swipe"}
                        onClick={() => setDisplayFormat("swipe")}
                        testid="format-swipe"
                        icon="↔️"
                        label="Swipe Before/After"
                        hint="Full photos, swipe"
                      />
                      <FormatTile
                        active={displayFormat === "slider"}
                        onClick={() => setDisplayFormat("slider")}
                        testid="format-slider"
                        icon="⇆"
                        label="Drag Slider"
                        hint="Compare with divider"
                      />
                    </div>
                  </div>

                  <Tabs value={uploadMode} onValueChange={setUploadMode} className="w-full mt-2">
                    <TabsList className="grid grid-cols-2 w-full">
                      <TabsTrigger value="upload" data-testid="mode-upload">📤 Upload Files</TabsTrigger>
                      <TabsTrigger value="url" data-testid="mode-url">🔗 Paste URLs</TabsTrigger>
                    </TabsList>

                    <form onSubmit={addGallery} className="space-y-4 mt-4">
                      <div>
                        <Label>Title</Label>
                        <Input
                          required
                          value={form.title}
                          onChange={(e) => setForm({ ...form, title: e.target.value })}
                          data-testid="gallery-title"
                        />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Input
                          required
                          placeholder="e.g. Complete Remodel"
                          value={form.category}
                          onChange={(e) => setForm({ ...form, category: e.target.value })}
                          data-testid="gallery-category"
                        />
                      </div>

                      <TabsContent value="upload" className="space-y-4 m-0">
                        {displayFormat === "single" ? (
                          <MultiPhotoPicker
                            files={singleFiles}
                            onChange={setSingleFiles}
                            testid="gallery-single-files"
                          />
                        ) : (
                          <>
                            <PhotoPicker
                              label="Before photo"
                              file={beforeFile}
                              onChange={setBeforeFile}
                              testid="gallery-before-file"
                            />
                            <PhotoPicker
                              label="After photo"
                              file={afterFile}
                              onChange={setAfterFile}
                              testid="gallery-after-file"
                            />
                          </>
                        )}
                      </TabsContent>

                      <TabsContent value="url" className="space-y-4 m-0">
                        {displayFormat === "single" ? (
                          <MultiUrlInput
                            urls={singleUrls}
                            onChange={setSingleUrls}
                            testid="gallery-single-urls"
                          />
                        ) : (
                          <>
                            <div>
                              <Label>Before image URL</Label>
                              <Input
                                required={uploadMode === "url"}
                                value={form.before_url}
                                onChange={(e) => setForm({ ...form, before_url: e.target.value })}
                                data-testid="gallery-before-url"
                                placeholder="https://…"
                              />
                            </div>
                            <div>
                              <Label>After image URL</Label>
                              <Input
                                required={uploadMode === "url"}
                                value={form.after_url}
                                onChange={(e) => setForm({ ...form, after_url: e.target.value })}
                                data-testid="gallery-after-url"
                                placeholder="https://…"
                              />
                            </div>
                          </>
                        )}
                      </TabsContent>

                      <div>
                        <Label>Description (optional)</Label>
                        <Textarea
                          value={form.description}
                          onChange={(e) => setForm({ ...form, description: e.target.value })}
                          data-testid="gallery-description"
                          placeholder="Brief project notes shown in the public gallery."
                        />
                      </div>

                      <DialogFooter>
                        <Button
                          type="submit"
                          disabled={uploading}
                          className="bg-[#0F3A5A] hover:bg-[#174E7A]"
                          data-testid="gallery-submit"
                        >
                          {uploading ? "Saving…" : "Save Project"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </Tabs>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {gallery.map((g) => (
                <div
                  key={g.id}
                  className={`relative bg-white rounded-2xl border overflow-hidden transition ${
                    g.featured ? "border-[#00B4D8] shadow-md ring-1 ring-[#00B4D8]/30" : "border-slate-200"
                  }`}
                  data-testid={`gallery-card-${g.id}`}
                >
                  {g.featured && (
                    <span
                      className="absolute top-3 left-3 z-10 bg-[#00B4D8] text-white text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full shadow"
                      data-testid={`featured-badge-${g.id}`}
                    >
                      ★ Featured
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleFeatured(g)}
                    aria-label={g.featured ? "Unfeature" : "Feature on homepage"}
                    data-testid={`feature-toggle-${g.id}`}
                    className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full grid place-items-center transition shadow ${
                      g.featured
                        ? "bg-[#00B4D8] text-white hover:bg-[#0096B4]"
                        : "bg-white/90 hover:bg-white text-slate-400 hover:text-[#00B4D8]"
                    }`}
                  >
                    <Star className={`w-4 h-4 ${g.featured ? "fill-white" : ""}`} strokeWidth={2} />
                  </button>
                  <div className="grid grid-cols-2 h-44">
                    <img
                      src={g.before_url || (g.images && g.images[0]) || ""}
                      alt="Before"
                      className="w-full h-full object-cover"
                    />
                    <img
                      src={g.after_url || (g.images && g.images[1]) || (g.images && g.images[0]) || ""}
                      alt="After"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#00B4D8] font-semibold">
                        {g.category}
                      </p>
                      <span
                        className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full"
                        data-testid={`format-badge-${g.id}`}
                      >
                        {g.display_format === "single"
                          ? `🖼️ ${(g.images || []).length || 1}`
                          : g.display_format === "swipe"
                          ? "↔ Swipe"
                          : "⇆ Slider"}
                      </span>
                    </div>
                    <h3 className="font-display font-semibold text-slate-900">{g.title}</h3>
                    {g.description && (
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{g.description}</p>
                    )}
                    <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-100">
                      <button
                        onClick={() => removeGallery(g.id)}
                        className="text-xs text-rose-600 hover:text-rose-700 flex items-center gap-1"
                        data-testid={`delete-${g.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                      <a
                        href={g.after_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> View
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="site-images" className="mt-6">
            <SiteImagesPanel />
          </TabsContent>
        </Tabs>
      </main>

      {/* Quote detail dialog */}
      <Dialog open={!!activeQuote} onOpenChange={(o) => !o && setActiveQuote(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="quote-detail-dialog">
          {activeQuote && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{activeQuote.name}</DialogTitle>
                <DialogDescription>
                  {activeQuote.service_interest} · {activeQuote.city}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 text-sm">
                {/* CRM controls */}
                <DetailGroup title="Pipeline Stage">
                  <div className="flex flex-wrap gap-2" data-testid="status-picker">
                    {STATUSES.map((s) => (
                      <button
                        key={s.key}
                        onClick={() => setEditStatus(s.key)}
                        data-testid={`set-status-${s.key}`}
                        className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition ${
                          editStatus === s.key
                            ? s.color + " ring-2 ring-offset-1 ring-[#00B4D8]"
                            : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4">
                    <label className="block text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500 mb-2">
                      Admin Notes (private)
                    </label>
                    <Textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Log call outcome, follow-up date, custom estimate amount, etc…"
                      rows={3}
                      data-testid="admin-notes-input"
                    />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      disabled={savingCrm}
                      onClick={async () => {
                        setSavingCrm(true);
                        try {
                          await api.patch(`/quote-requests/${activeQuote.id}`, {
                            status: editStatus,
                            admin_notes: editNotes,
                          });
                          // Update local state
                          setQuotes((qs) =>
                            qs.map((q) =>
                              q.id === activeQuote.id
                                ? { ...q, status: editStatus, admin_notes: editNotes }
                                : q
                            )
                          );
                          setActiveQuote((a) => ({ ...a, status: editStatus, admin_notes: editNotes }));
                          toast.success("Updated");
                        } catch (e) {
                          toast.error("Failed to save");
                        } finally {
                          setSavingCrm(false);
                        }
                      }}
                      className="bg-[#0F3A5A] hover:bg-[#174E7A]"
                      data-testid="save-crm-btn"
                    >
                      <Save className="w-3.5 h-3.5 mr-1.5" />
                      {savingCrm ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </DetailGroup>

                <DetailGroup title="Contact">
                  <DetailRow label="Phone" value={<a href={`tel:${activeQuote.phone}`} className="text-[#0F3A5A] hover:underline">{activeQuote.phone}</a>} />
                  <DetailRow label="Email" value={<a href={`mailto:${activeQuote.email}`} className="text-[#0F3A5A] hover:underline">{activeQuote.email}</a>} />
                  <DetailRow label="Address" value={activeQuote.address || "—"} />
                </DetailGroup>

                <DetailGroup title="Project">
                  <DetailRow label="Pool Age" value={activeQuote.pool_age || "—"} />
                  <DetailRow label="Pool Size" value={activeQuote.pool_size || "—"} />
                  <DetailRow label="Finish" value={activeQuote.current_finish || "—"} />
                  <DetailRow
                    label="Reasons"
                    value={
                      (activeQuote.project_reasons || []).length
                        ? activeQuote.project_reasons.join(", ")
                        : "—"
                    }
                  />
                  <DetailRow
                    label="Upgrades"
                    value={
                      (activeQuote.desired_upgrades || []).length
                        ? activeQuote.desired_upgrades.join(", ")
                        : "—"
                    }
                  />
                </DetailGroup>

                <DetailGroup title="Timeline & Budget">
                  <DetailRow label="Timeline" value={activeQuote.timeline || "—"} />
                  <DetailRow label="Budget" value={activeQuote.budget || "—"} />
                </DetailGroup>

                {activeQuote.notes && (
                  <DetailGroup title="Notes">
                    <p className="text-slate-700 whitespace-pre-wrap">{activeQuote.notes}</p>
                  </DetailGroup>
                )}

                {activeQuote.photos && activeQuote.photos.length > 0 && (
                  <DetailGroup title={`Photos (${activeQuote.photos.length})`}>
                    <div className="grid grid-cols-2 gap-3">
                      {activeQuote.photos.map((p, i) => (
                        <a
                          key={i}
                          href={`data:${p.content_type};base64,${p.data}`}
                          target="_blank"
                          rel="noreferrer"
                          className="block rounded-xl overflow-hidden border border-slate-200 aspect-video bg-slate-100"
                        >
                          <img
                            src={`data:${p.content_type};base64,${p.data}`}
                            alt={p.filename}
                            className="w-full h-full object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  </DetailGroup>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailGroup({ title, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#00B4D8] mb-2">{title}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 py-1">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 font-medium">{value}</span>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#0F3A5A]/5 text-[#0F3A5A] grid place-items-center">
        {icon}
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</p>
        <p className="font-display text-2xl font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  );
}

function PhotoPicker({ label, file, onChange, testid }) {
  const [preview, setPreview] = useState(null);
  const id = `photo-input-${testid}`;

  const handlePick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5MB or smaller");
      return;
    }
    onChange(f);
    setPreview(URL.createObjectURL(f));
  };

  const clear = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    onChange(null);
  };

  return (
    <div>
      <Label>{label}</Label>
      <input
        id={id}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handlePick(e.target.files?.[0])}
        data-testid={testid}
      />
      {!file ? (
        <label
          htmlFor={id}
          className="mt-1 flex items-center justify-center gap-3 border-2 border-dashed border-slate-300 hover:border-[#00B4D8] rounded-xl py-6 px-4 cursor-pointer transition group"
        >
          <Upload className="w-4 h-4 text-slate-500 group-hover:text-[#00B4D8]" />
          <span className="text-sm text-slate-600 group-hover:text-[#0F3A5A] font-medium">
            Click to upload (max 5MB)
          </span>
        </label>
      ) : (
        <div className="mt-1 relative rounded-xl overflow-hidden border border-slate-200 bg-slate-100 aspect-video">
          {preview && (
            <img src={preview} alt={label} className="w-full h-full object-cover" />
          )}
          <button
            type="button"
            onClick={clear}
            aria-label="Remove photo"
            data-testid={`${testid}-remove`}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 hover:bg-black text-white grid place-items-center"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.18em] font-semibold bg-white/90 text-slate-700 px-2 py-1 rounded-full">
            {file.name.slice(0, 28)}
          </span>
        </div>
      )}
    </div>
  );
}


function FormatTile({ active, onClick, testid, icon, label, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`p-3 rounded-xl border text-left transition ${
        active
          ? "border-[#0F3A5A] bg-[#0F3A5A] text-white shadow-md"
          : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
      }`}
    >
      <span className="text-xl">{icon}</span>
      <p className="text-xs font-display font-semibold mt-1.5 leading-tight">{label}</p>
      <p className={`text-[10px] mt-0.5 ${active ? "text-slate-200" : "text-slate-500"}`}>
        {hint}
      </p>
    </button>
  );
}

function MultiPhotoPicker({ files, onChange, testid }) {
  const [previews, setPreviews] = useState([]);
  const id = `multi-photo-input-${testid}`;

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    const remaining = 10 - files.length;
    const next = arr.slice(0, remaining).filter((f) => {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} is not an image`);
        return false;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 5MB`);
        return false;
      }
      return true;
    });
    const nextFiles = [...files, ...next];
    onChange(nextFiles);
    setPreviews((p) => [...p, ...next.map((f) => URL.createObjectURL(f))]);
  };

  const remove = (idx) => {
    onChange(files.filter((_, i) => i !== idx));
    setPreviews((p) => {
      if (p[idx]) URL.revokeObjectURL(p[idx]);
      return p.filter((_, i) => i !== idx);
    });
  };

  return (
    <div>
      <Label>Photos (up to 10)</Label>
      <input
        id={id}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => addFiles(e.target.files)}
        data-testid={testid}
      />
      <label
        htmlFor={id}
        className="mt-1 flex items-center justify-center gap-3 border-2 border-dashed border-slate-300 hover:border-[#00B4D8] rounded-xl py-6 px-4 cursor-pointer transition group"
      >
        <Upload className="w-4 h-4 text-slate-500 group-hover:text-[#00B4D8]" />
        <span className="text-sm text-slate-600 group-hover:text-[#0F3A5A] font-medium">
          {files.length === 0
            ? "Click to upload images (max 5MB each)"
            : `Add more (${files.length}/10)`}
        </span>
      </label>
      {previews.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-3" data-testid={`${testid}-previews`}>
          {previews.map((p, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
              <img src={p} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label="Remove"
                data-testid={`${testid}-remove-${i}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-black text-white grid place-items-center"
              >
                <X className="w-3 h-3" />
              </button>
              <span className="absolute bottom-1 left-1 bg-white/85 text-[10px] font-semibold px-1.5 py-0.5 rounded">
                {i + 1}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MultiUrlInput({ urls, onChange, testid }) {
  const update = (i, v) => {
    const next = [...urls];
    next[i] = v;
    onChange(next);
  };
  const add = () => onChange([...urls, ""]);
  const remove = (i) => onChange(urls.filter((_, idx) => idx !== i));

  return (
    <div data-testid={testid}>
      <Label>Image URLs (one per slide)</Label>
      <div className="space-y-2 mt-1">
        {urls.map((u, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input
              value={u}
              onChange={(e) => update(i, e.target.value)}
              placeholder={`https://… (image ${i + 1})`}
              data-testid={`${testid}-${i}`}
            />
            {urls.length > 1 && (
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label="Remove URL"
                className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-rose-50 text-slate-500 hover:text-rose-600 grid place-items-center shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-2 text-xs font-semibold text-[#0F3A5A] hover:text-[#00B4D8] flex items-center gap-1"
        data-testid={`${testid}-add`}
      >
        <Plus className="w-3.5 h-3.5" /> Add another image URL
      </button>
    </div>
  );
}


const SITE_IMAGE_SLOTS = [
  {
    key: "logo",
    title: "Logo",
    hint: "Transparent PNG works best. Replaces the 'AZ' monogram in the header and footer.",
    aspect: "aspect-[16/9]",
  },
  {
    key: "hero_background",
    title: "Hero Background",
    hint: "Full-width image behind the homepage headline. Landscape orientation, ≥1920×1080 looks best.",
    aspect: "aspect-[16/9]",
  },
  {
    key: "about_image_1",
    title: "About — Left Photo",
    hint: "Tall portrait photo (3:4 aspect). Shown next to the About section text.",
    aspect: "aspect-[3/4]",
  },
  {
    key: "about_image_2",
    title: "About — Right Photo",
    hint: "Tall portrait photo (3:4 aspect). Sits beside the left photo with offset.",
    aspect: "aspect-[3/4]",
  },
];

function SiteImagesPanel() {
  const [images, setImages] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/site-images");
      setImages(data || {});
    } catch (_) {
      setImages({});
    }
  };

  useEffect(() => {
    load();
  }, []);

  const upload = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5MB or smaller");
      return;
    }
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      await api.post(`/site-images/${key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      toast.success("Image updated — refresh the homepage to see it live");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setBusy(null);
    }
  };

  const revert = async (key) => {
    if (!confirm("Revert to the default image?")) return;
    setBusy(key);
    try {
      await api.delete(`/site-images/${key}`);
      await load();
      toast.success("Reverted to default");
    } catch (_) {
      toast.error("Revert failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-xl font-semibold text-slate-900">Site Images</h2>
        <p className="text-sm text-slate-500 mt-1">
          Swap the photos shown on the homepage hero and About section. Leave a slot empty to use the default stock image.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {SITE_IMAGE_SLOTS.map((slot) => {
          const url = images[slot.key];
          const isBusy = busy === slot.key;
          const inputId = `site-img-${slot.key}`;
          return (
            <div
              key={slot.key}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
              data-testid={`site-image-card-${slot.key}`}
            >
              <div className={`${slot.aspect} bg-slate-100 relative`}>
                {url ? (
                  <img src={url} alt={slot.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-slate-400 text-xs uppercase tracking-[0.2em] font-semibold">
                    Default in use
                  </div>
                )}
                {url && (
                  <span className="absolute top-2 right-2 text-[10px] uppercase tracking-[0.18em] font-bold bg-[#00B4D8] text-white px-2 py-0.5 rounded-full">
                    Custom
                  </span>
                )}
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <p className="font-display font-semibold text-slate-900 text-sm">{slot.title}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{slot.hint}</p>
                </div>
                <input
                  id={inputId}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => upload(slot.key, e.target.files?.[0])}
                  data-testid={`site-image-input-${slot.key}`}
                />
                <div className="flex gap-2">
                  <label
                    htmlFor={inputId}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 bg-[#0F3A5A] hover:bg-[#174E7A] text-white text-xs font-semibold py-2 rounded-lg cursor-pointer transition ${
                      isBusy ? "opacity-60 pointer-events-none" : ""
                    }`}
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {isBusy ? "Uploading…" : url ? "Replace" : "Upload"}
                  </label>
                  {url && (
                    <button
                      type="button"
                      onClick={() => revert(slot.key)}
                      disabled={isBusy}
                      data-testid={`site-image-revert-${slot.key}`}
                      className="text-xs text-rose-600 hover:text-rose-700 font-semibold px-3 disabled:opacity-50"
                    >
                      Revert
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <HeroCopyPanel />
    </div>
  );
}

function HeroCopyPanel() {
  const [headline, setHeadline] = useState("");
  const [subheadline, setSubheadline] = useState("");
  const [original, setOriginal] = useState({ headline: "", subheadline: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/site-content");
      setHeadline(data?.hero_headline || "");
      setSubheadline(data?.hero_subheadline || "");
      setOriginal({
        headline: data?.hero_headline || "",
        subheadline: data?.hero_subheadline || "",
      });
    } catch (_) {
      // ignore
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dirty =
    headline !== original.headline || subheadline !== original.subheadline;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (headline.trim()) payload.hero_headline = headline.trim();
      if (subheadline.trim()) payload.hero_subheadline = subheadline.trim();
      if (Object.keys(payload).length === 0) {
        toast.error("Add at least one field");
        setSaving(false);
        return;
      }
      await api.patch("/site-content", payload);
      setOriginal({ headline: headline.trim(), subheadline: subheadline.trim() });
      toast.success("Hero copy updated — refresh the homepage to see it live");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const revertField = async (field) => {
    if (!confirm(`Revert ${field === "hero_headline" ? "headline" : "subheadline"} to default?`)) return;
    try {
      await api.delete(`/site-content/${field}`);
      if (field === "hero_headline") {
        setHeadline("");
        setOriginal((o) => ({ ...o, headline: "" }));
      } else {
        setSubheadline("");
        setOriginal((o) => ({ ...o, subheadline: "" }));
      }
      toast.success("Reverted to default");
    } catch (_) {
      toast.error("Revert failed");
    }
  };

  return (
    <div className="mt-10 pt-10 border-t border-slate-200" data-testid="hero-copy-panel">
      <div className="mb-5">
        <h2 className="font-display text-xl font-semibold text-slate-900">Hero Copy</h2>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Override the homepage headline and subheadline. Leave a field empty to use
          the default. (Note: setting a custom headline uses plain text — the italic
          "Luxury Oasis" styling is replaced when you provide your own.)
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5 max-w-3xl">
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              Hero Headline
            </Label>
            {original.headline && (
              <button
                type="button"
                onClick={() => revertField("hero_headline")}
                className="text-[11px] font-semibold text-rose-600 hover:text-rose-700"
                data-testid="revert-headline"
              >
                Revert
              </button>
            )}
          </div>
          <Textarea
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="Transform Your Backyard Into a Luxury Oasis"
            rows={2}
            data-testid="hero-headline-input"
          />
          <p className="text-xs text-slate-400 mt-1">
            Used as plain text. Max 400 characters.
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              Hero Subheadline
            </Label>
            {original.subheadline && (
              <button
                type="button"
                onClick={() => revertField("hero_subheadline")}
                className="text-[11px] font-semibold text-rose-600 hover:text-rose-700"
                data-testid="revert-subheadline"
              >
                Revert
              </button>
            )}
          </div>
          <Textarea
            value={subheadline}
            onChange={(e) => setSubheadline(e.target.value)}
            placeholder="Expert pool construction, renovations, equipment upgrades & custom enhancements throughout South Florida."
            rows={3}
            data-testid="hero-subheadline-input"
          />
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="bg-[#0F3A5A] hover:bg-[#174E7A]"
            data-testid="save-hero-copy"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Saving…" : "Save Hero Copy"}
          </Button>
        </div>
      </div>
    </div>
  );
}
